//@import dependencies
import React, {useState, useEffect} from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Modal } from 'react-bootstrap'
import { useForm } from 'react-hook-form'
import {BiHappyAlt} from 'react-icons/bi'
import { BsFileEarmarkRuled } from "react-icons/bs"
//@end

//@import components
import IconComponent from 'app_components/commons/icons'
import AppContainer from 'app_components/layouts/app'
import BreadcrumbComponent from 'app_components/commons/breadcrumb'
import DataTableComponent from 'app_components/commons/dataTable'
import CourseSchedulingForm from 'app_components/modules/admin/courseScheduling/form'
import CourseSchedulingSkeleton from './courseSchedulingSkeleton'
import CourseSchedulingScreenFormFilterLayout from './courseSchedulingScreenFormFilterLayout'
import { GoFileSubmodule } from 'react-icons/go'
import { VscFileSubmodule } from 'react-icons/vsc'
//@end

// @import hooks
import {useColors} from 'app_hooks/useColors'
// @end

// @INFO Import types
import { IPagination } from 'app_types/pagination/paginationTypes'
import { CourseSchedulingProvisioningMoodleStatus, ForceStatus, ICourseSchedulingListItem } from 'app_types/admin/courseScheduling/courseSchedulingTypes'
import { ICourseSchedulingDetailsHttpResponse } from 'app_types/admin/courseScheduling/courseSchedulingDetailsTypes'
// @end

// @INFO Import services
import CourseSchedulingDetailsService from 'app_services/admin/courseScheduling/courseSchedulingDetailsService'
import { routingService } from 'app_services/routingService'
import CourseSchedulingService from 'app_services/admin/courseScheduling/courseSchedulingService'
// @end

// @INFO Import Styles
import './../courseSchedulingScreen.scss'
import TooltipComponent from 'app_components/commons/TooltipComponent/TooltipComponent'
import ModalAttached from 'app_components/modules/ModalAttached/ModalAttached'
import { HiDocumentReport } from 'react-icons/hi'
import * as AttachedTypes from 'app_types/admin/attached/attached.types';
import { FaRegClone } from 'react-icons/fa'
import ModalCourseSchedulingAssociate from 'app_components/modules/ModalCourseSchedulingAssociate/ModalCourseSchedulingAssociate'
import UserService from 'app_services/user/userService'
import { AiOutlineGroup, AiOutlineReload } from 'react-icons/ai'
import Swal from 'sweetalert2'
import { ScreenNotificationsUtil } from 'app_utils/screenNotification';
import CourseSchedulingMoodleStatus from './CourseSchedulingMoodleStatus'

// @ADD Component types
interface ICourseSchedulingScreenLayoutProps {
  itemList: ICourseSchedulingListItem[]
  status: null | 'loading' | 'error'
  add: boolean
  edit: boolean
  view: boolean
  editData: any
  selectedRow: null | string
  pagination: IPagination
  addItem: any
  updateItem: any
  showAddForm: (add: boolean) => void
  showEditForm: (edit: boolean, editData?: any) => void
  onViewerRow: (view: boolean, editData?: any) => void
  onPageChange: (page: number) => void
  onRowClick: (row: any) => void
  removeItem: (userId: string) => void
  onSearch: () => void
  replaceSchedulingItem: (courseScheduling: any) => void
  searchFormInputs?: any
  refreshPage?: () => void
}

const CourseSchedulingScreenLayout: React.FC<ICourseSchedulingScreenLayoutProps> = (props) => {

  const userService: UserService = new UserService()
  const courseSchedulingService: CourseSchedulingService = new CourseSchedulingService()
  const screenNotification = new ScreenNotificationsUtil();

  const searchForm = useForm()
  const [colors] = useColors()

  const { t } = useTranslation()

  const [reportData, setReportData] = useState<null | {status: 'loading' | 'success', path: string}>(null)
  const [generateReportModal, setGenerateReportModal] = useState<boolean>(false)
  const [selectCourseSchedulingDetail, setSelectCourseSchedulingDetail] = useState<string | null>(null)
  const [openAttached, updateOpenAttached] = useState<string | undefined>(undefined);
  const [openAttachedStudent, updateOpenAttachedStudent] = useState<string | undefined>(undefined);
  const [openBusinessReport, updateOpenBusinessReport] = useState<any>(undefined);
  const [openPartialReport, updateOpenPartialReport] = useState<any>(undefined);
  const [observations, updateObservations] = useState<string | undefined>(undefined);
  const [modalCourseSchedulingAssociate, setModalCourseSchedulingAssociate] = useState<{status: boolean, courseSchedulingId: string | null}>({status: true, courseSchedulingId: null})

  const hideGenerateReportModal = () => {
    setGenerateReportModal(false)
  }

  const openGenerateReportModal = async (courseSchedulingId: string | null = null, options: {type: 'single' | 'general', format: 'xlsx' | 'pdf'}) => {
    setGenerateReportModal(true)
    setReportData({status: 'loading', path: ''})
    const report = await courseSchedulingService.generatePdfReport({
      course_scheduling: courseSchedulingId,
      ...options
    })
    if (report?.path) {
      setReportData({status: 'success', path: report.path})
    } else {
      setGenerateReportModal(false)
      setReportData(null)
    }
  }

  const openGenerateSurveyReportModal = async (courseScheduling: any, options: {output_format: 'xlsx' | 'json'}) => {
    let report = null
    if (courseScheduling?.schedulingMode?.name) {
      if (courseScheduling?.schedulingMode?.name === 'Virtual') {
        setGenerateReportModal(true)
        setReportData({status: 'loading', path: ''})
        report = await courseSchedulingService.generateSurveyReport({
          course_scheduling: courseScheduling._id,
          ...options
        })
        if (report?.path) {
          setReportData({status: 'success', path: report.path})
        } else {
          setGenerateReportModal(false)
          setReportData(null)
        }
      } else {
        setSelectCourseSchedulingDetail(courseScheduling._id)
      }
    }

  }
  const closeModalSelectCourse = async (courseSchedulingDetail: string | null) => {
    if (courseSchedulingDetail) {
      setGenerateReportModal(true)
      setReportData({status: 'loading', path: ''})
      const report = await courseSchedulingService.generateSurveyReport({
        course_scheduling_detail: courseSchedulingDetail,
        output_format: 'xlsx'
      })
      if (report?.path) {
        setReportData({status: 'success', path: report.path})
      } else {
        setGenerateReportModal(false)
        setReportData(null)
      }
    }
    setSelectCourseSchedulingDetail(null)
  }

  const handleClickAttached = (id: string, observations: string) => {
    updateOpenAttached(id);
    updateObservations(observations ? observations : undefined)
  }

  const handleClickBusinessReport = (courseScheduling: any) => {
    updateOpenBusinessReport(courseScheduling);
  }

  const handleClickPartialReport = (courseScheduling: any) => {
    updateOpenPartialReport(courseScheduling);
  }

  const onBusinessReportSaved = async (attached: AttachedTypes.IAttached) => {
    if (!attached || openBusinessReport?.business_report) return;
    // Actualizar el courseScheduling con el reporte empresarial
    const response = await courseSchedulingService.insertOrUpdate({
        business_report: attached._id
      },
      `update/${openBusinessReport._id}`
    );
    if (response) {
      props.replaceSchedulingItem(response);
    }
  }

  const onPartialReportSaved = async (attached: AttachedTypes.IAttached) => {
    if (!attached || openPartialReport?.partial_report) return;
    // Actualizar el courseScheduling con el reporte parcial
    const response = await courseSchedulingService.insertOrUpdate({
        partial_report: attached._id
      },
      `update/${openPartialReport._id}`
    );
    if (response) {
      props.replaceSchedulingItem(response);
    }
  }

  const handleModalCourseSchedulingAssociate = (courseScheduling: ICourseSchedulingListItem) => {
    if (courseScheduling?._id) {
      setModalCourseSchedulingAssociate({
        status: true,
        courseSchedulingId: courseScheduling._id
      })
    }
  }
  const onCloseModalCourseSchedulingAssociate = (data: any) => {
    setModalCourseSchedulingAssociate({
      status: false,
      courseSchedulingId: null
    })
    if (data?.status === 'saved' && props.refreshPage) {
      props.refreshPage()
    }
  }

  const handleClockClone = async (_courseScheduling: any) => {
    const response = await Swal.fire({
      title: '¿Clonar Servicio?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#002e5d',
      cancelButtonColor: 'rgba(0, 133, 202, 1)',
      confirmButtonText: 'Clonar'
    })

    if (response?.isConfirmed) {
      Swal.fire({
        title: 'Clonando servicio',
        html: 'Por favor espere mientras el proceso termina...',
        allowOutsideClick: false,
        onBeforeOpen: () => {
            Swal.showLoading()
        },
      });
      const response = await courseSchedulingService.duplicateService({id: _courseScheduling?._id});
      Swal.close();
      if (response?.code === 200) {
        props.onSearch()
        const serviceId = response?.service?.serviceId;
        await Swal.fire(
          'Servicio clonado con éxito',
          `Identificador del servicio: ${serviceId}`,
          'success'
        )
      } else {
        screenNotification.showSuccessTimer({icon: 'error', text: 'Ha ocurrido un error inesperado'});
      }
    }
  }

  const handleClickReactivate = async (_courseScheduling: any) => {
    const response = await Swal.fire({
      title: '¿Reactivar Servicio?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#002e5d',
      cancelButtonColor: 'rgba(0, 133, 202, 1)',
      confirmButtonText: 'Reactivar'
    })

    if (response?.isConfirmed) {
      Swal.fire({
        title: 'Reactivando servicio',
        html: 'Por favor espere mientras el proceso termina...',
        allowOutsideClick: false,
        onBeforeOpen: () => {
            Swal.showLoading()
        },
      });
      const response = await courseSchedulingService.reactivateService({id: _courseScheduling?._id});
      Swal.close();
      if (response?.code === 200) {
        screenNotification.showSuccessTimer();
        props.onSearch()
      } else {
        screenNotification.showSuccessTimer({icon: 'error', text: 'Ha ocurrido un error inesperado'});
      }
    }
  }

  const handleClickForceState= async (_courseScheduling: any, state: ForceStatus) => {
    let stateLabel = ''
    switch (state) {
      case 'programmed':
        stateLabel = 'programado'
        break;
      default:
        break;
    }
    const message = `¿Desea cambiar el estado del servicio a ${stateLabel}?`
    const response = await Swal.fire({
      title: message,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#002e5d',
      cancelButtonColor: 'rgba(0, 133, 202, 1)',
      confirmButtonText: 'Aceptar'
    })

    if (response?.isConfirmed) {
      Swal.fire({
        title: 'Cambiando estado',
        html: 'Por favor espere mientras el proceso termina...',
        allowOutsideClick: false,
        onBeforeOpen: () => {
            Swal.showLoading()
        },
      });
      const response = await courseSchedulingService.forceStatusService({id: _courseScheduling?._id, status: state});
      Swal.close();
      if (response?.code === 200) {
        screenNotification.showSuccessTimer();
        props.onSearch()
      } else {
        screenNotification.showSuccessTimer({icon: 'error', text: 'Ha ocurrido un error inesperado'});
      }
    }
  }

  const getItemsToRender = () => {
    if (!props.itemList?.length) return []
    return props.itemList.map((item) => ({
      ...item,
      component_render_moodle_status: ( <CourseSchedulingMoodleStatus courseScheduling={item} onServiceUpdated={props.refreshPage} /> )
    }))
  }

  const shouldDisableMoodleActions = (courseScheduling: ICourseSchedulingListItem) => {
    if (
      courseScheduling?.provisioningMoodle?.status === CourseSchedulingProvisioningMoodleStatus.ERROR
      || courseScheduling?.provisioningMoodle?.status === CourseSchedulingProvisioningMoodleStatus.IN_PROCESS
    ) {
      return true
    } else {
      return false
    }
  }

  return(
    <AppContainer>
      <div className='course_scheduling-component container-fluid'>
        <BreadcrumbComponent links={['#']} texts={[`${t('components.breadcrumb.course_scheduling')}`]} />

        <div className='course-scheduling-screen'>

          {props.status === 'loading' && (
            <CourseSchedulingSkeleton />
          )}

          {props.status !== 'loading' && !props.add && !props.edit && !props.view && (
            <>
              <h2>{t('screens.course_scheduling.title')}</h2>

              <div className="mb-3">
                <CourseSchedulingScreenFormFilterLayout
                  searchForm={searchForm}
                  searchFormInputs={props.searchFormInputs}
                  onSearch={props.onSearch}
                  fields={[
                    'service_id',
                    'course_scheduling_code',
                    'mode',
                    'schedulingType',
                    'schedulingStatus',
                    'regional',
                    'client',
                    'schedulingAssociation',
                    'teacher'
                  ]}
                />
              </div>

              <DataTableComponent
                data={getItemsToRender()}
                headerKeys={[
                  `${t('components.dataTable.details.course_scheduling.service_id')}`,
                  `${t('components.dataTable.headers.course_scheduling.schedulingCode')}`,
                  `${t('components.dataTable.headers.course_scheduling.schedulingMode')}`,
                  `${t('components.dataTable.headers.course_scheduling.program')}`,
                  // `${t('components.dataTable.headers.course_scheduling.course')}`,
                  // `${t('components.dataTable.headers.course_scheduling.teacher')}`,
                  `${t('components.dataTable.headers.course_scheduling.schedulingStatus')}`,
                  // `${t('components.dataTable.headers.course_scheduling.regional')}`,
                  `${t('components.dataTable.headers.course_scheduling.startDate')}`,
                  `${t('components.dataTable.headers.course_scheduling.endDate')}`,
                  `${t('components.dataTable.headers.course_scheduling.schedulingAssociationSlug')}`,
                  'Estado moodle',
                ]}
                keys={[
                  'metadata.service_id',
                  'program.code',
                  'schedulingMode.name',
                  'program.name',
                  'schedulingStatus.name',
                  'startDate',
                  'endDate',
                  'schedulingAssociation.slug',
                  'component_render_moodle_status'
                ]}
                tooltipKeys={[
                  'program.name'
                ]}
                selectedRow={props.selectedRow}
                pagination={props.pagination}
                detailsKey={[
                  // { name: `${t('components.dataTable.details.course_scheduling.observations')}`, key: 'observations' },
                  { name: `${t('components.dataTable.details.course_scheduling.coordinator')}`, key: 'metadata.user.fullname' },
                  { name: `${t('components.dataTable.details.course_scheduling.service_date')}`, key: 'metadata.date' },
                  { name: `${t('components.dataTable.details.course_scheduling.service_year')}`, key: 'metadata.year' },
                ]}
                // onSearch={props.onSearch}
                onAddRow={props.showAddForm}
                onClickRow={props.onRowClick}
                onRemoveRow={props.removeItem}
                onPageChange={props.onPageChange}
                onEditRow={props.showEditForm}
                onViewerRow={props.onViewerRow}
                headerButtons={() => {
                  return (
                    <>
                      <button
                        className='btn btn-link btn-sm'
                        data-tip={t('labels.generate_report')}
                        onClick={() => openGenerateReportModal(null, {type: 'general', format: 'xlsx'})}
                      >
                        {/* <IconComponent svg='Report' width='20px' height='20px' color={colors.primary} /> */}
                        <IconComponent fontawesome={{icon: 'fas fa-file-excel'}} width='20px' height='20px' color={colors.primary} />&nbsp;&nbsp;
                      </button>

                    </>
                  )
                }}
                rowButtons={(courseScheduling) => {
                  return (
                    <>
                      {
                        userService.can('permission:course_scheduling_force_change_confirmed_to_programmed', 'module:course_scheduling') &&
                        ['Confirmado', 'Ejecutado'].includes(courseScheduling?.schedulingStatus?.name) ? (
                          <>
                            <TooltipComponent
                              text={t('screens.course_scheduling.actions.back_to_programming')}
                              className='tooltip-icon-scheduling-screen'
                            >
                              <AiOutlineReload
                                onClick={() => handleClickForceState(courseScheduling, ForceStatus.PROGRAMMED)}
                                className='icon-scheduling-screen'
                              />
                            </TooltipComponent>
                          </>
                        ) : null
                      }
                      {
                        userService.can('permission:course_scheduling_associations_view', 'module:course_scheduling') &&
                        (
                          !courseScheduling?.schedulingAssociation ||
                          (courseScheduling?.schedulingAssociation && courseScheduling?.schedulingAssociation?.associationType === 'parent')
                        ) && !['Cancelado', 'Ejecutado'].includes(courseScheduling?.schedulingStatus?.name) ? (
                          <>
                            <TooltipComponent
                              text='Asociar programaciones'
                              className='tooltip-icon-scheduling-screen'
                            >
                              <AiOutlineGroup
                                onClick={() => handleModalCourseSchedulingAssociate(courseScheduling)}
                                className='icon-scheduling-screen'
                              />
                            </TooltipComponent>
                          </>
                        )
                        : null
                      }

                      {
                      !shouldDisableMoodleActions(courseScheduling) &&
                      userService.can('permission:course_scheduling_create', 'module:course_scheduling') ?
                        <TooltipComponent
                          text='Clonar servicio'
                          className='tooltip-icon-scheduling-screen'
                        >
                          <FaRegClone
                            onClick={() => handleClockClone(courseScheduling)}
                            className='icon-scheduling-screen'
                          />
                        </TooltipComponent>
                      :null}

                      {userService.can('permission:course_scheduling_reactivate', 'module:course_scheduling') && courseScheduling?.schedulingStatus?.name === 'Cancelado' ?
                        <TooltipComponent
                          text='Reactivar servicio'
                          className='tooltip-icon-scheduling-screen'
                        >
                          <AiOutlineReload
                            onClick={() => handleClickReactivate(courseScheduling)}
                            className='icon-scheduling-screen'
                          />
                        </TooltipComponent>
                      :null}

                      <TooltipComponent
                        text='Informe empresarial final'
                        className='tooltip-icon-scheduling-screen'
                      >
                        <HiDocumentReport
                          onClick={() => handleClickBusinessReport(courseScheduling)}
                          className='icon-scheduling-screen'
                        />
                      </TooltipComponent>
                      <TooltipComponent
                        text='Informe empresarial parcial'
                        className='tooltip-icon-scheduling-screen'
                      >
                        <HiDocumentReport
                          onClick={() => handleClickPartialReport(courseScheduling)}
                          className='icon-scheduling-screen'
                        />
                      </TooltipComponent>
                      {courseScheduling.attachments ?
                        <TooltipComponent
                          text='Adjuntos de operación'
                          className='tooltip-icon-scheduling-screen'
                        >
                          <GoFileSubmodule
                            onClick={() => handleClickAttached(courseScheduling.attachments, courseScheduling.observations)}
                            className='icon-scheduling-screen'
                          />
                        </TooltipComponent>
                      :null}
                      {courseScheduling.attachments_student ?
                        <TooltipComponent
                          text='Adjuntos de estudiantes'
                          className='tooltip-icon-scheduling-screen'
                        >
                          <VscFileSubmodule
                            onClick={() => updateOpenAttachedStudent(courseScheduling.attachments_student)}
                            className='icon-scheduling-screen'
                          />
                        </TooltipComponent>
                      :null}
                      {!shouldDisableMoodleActions(courseScheduling) && (
                        <Link
                          className='btn btn-link btn-sm'
                          data-tip={t('labels.programming')}
                          to={routingService.getSecurePath(`/admin/course-scheduling/${courseScheduling._id}`)}
                        >
                          <IconComponent svg='Programming' width='24px' height='24px' color={colors.primary} />
                        </Link>
                      )}
                      {!shouldDisableMoodleActions(courseScheduling) && (
                        <Link
                          className='btn btn-link btn-sm'
                          data-tip={t('labels.participants')}
                          to={routingService.getSecurePath(`/admin/course-scheduling-participants/${courseScheduling._id}`)}
                        >
                          <IconComponent svg='User' width='20px' height='20px' color={colors.primary} />
                        </Link>
                      )}
                      <button
                        className='btn btn-link btn-sm'
                        data-tip={t('labels.generate_report')}
                        onClick={() => openGenerateReportModal(courseScheduling._id, {type: 'single', format: 'pdf'})}
                      >
                        <IconComponent svg='Report' width='20px' height='20px' color={colors.primary} />
                      </button>
                      <button
                        className='btn btn-link btn-sm'
                        data-tip={t('labels.survey_report')}
                        onClick={() => openGenerateSurveyReportModal(courseScheduling, {output_format: 'xlsx'})}
                      >
                        {/* <IconComponent svg='Report' width='20px' height='20px' color={colors.primary} /> */}
                        <BiHappyAlt size="20px" color={colors.primary} />
                      </button>
                    </>
                  )
                }}

                onModal={null}
              />
            </>
          )}

          {props.add && (
            <div className="course-scheduling-container-form col-12 mb-3">
              <div className="d-flex align-items-center mb-2">
                <h2 className="form-title">
                  {t("screens.course_scheduling.add_layout.title_page")}
                </h2>
                <button className="btn btn-light btn-sm border ml-2" onClick={() => props.showAddForm(false)}>
                  {t("buttons.back")}
                </button>
              </div>
              <CourseSchedulingForm
                onHandleSubmit={props.addItem}
                viewer={false}
              />
            </div>
          )}

          {(props.edit || props.view) && props.editData && (
            <div className="course-scheduling-container-form mb-3">
              <div className="d-flex align-items-center mb-2">
                {props.edit && (
                  <>
                    <h2 className="form-title">
                      {t("screens.course_scheduling.edit_layout.title_page")}
                    </h2>
                    <button className="btn btn-light btn-sm border ml-2" onClick={() => props.showEditForm(false)}>
                      {t("buttons.back")}
                    </button>
                  </>
                )}
                {props.view && (
                  <>
                    <h2 className="form-title">
                      {t("screens.course_scheduling.view_layout.title_page")}
                    </h2>
                    <button className="btn btn-light btn-sm border ml-2" onClick={() => props.onViewerRow(false)}>
                      {t("buttons.back")}
                    </button>
                  </>
                )}
              </div>
              <CourseSchedulingForm
                editData={{...props.editData, details: props.editData}}
                onHandleSubmit={props.updateItem}
                viewer={props.editData?.schedulingStatus?.name === 'Cancelado' ||  props.view}
              />
            </div>
          )}

          <Modal show={generateReportModal} onHide={() => hideGenerateReportModal()}>
            <Modal.Header closeButton>
              <Modal.Title>{t('screens.course_scheduling.generate_report.modal.title')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {reportData?.status === 'loading' && (
                <div className='modal-report-status'>
                  <div>
                    <div className='spinner-border text-primary' role='status'>
                      <span className='sr-only'>{t('loading')}</span>
                    </div>
                  </div>
                  <div>
                    <p>{t('labels.generating_report')}...</p>
                  </div>
                </div>
              )}
              {reportData?.status === 'success' && reportData.path !== '' && (
                <div className='modal-report-status'>
                  <a href={reportData.path} download rel='noreferrer' target='_blank' onClick={() => hideGenerateReportModal()}>
                    {t('labels.download_report')}
                  </a>
                </div>
              )}
            </Modal.Body>
          </Modal>

          {selectCourseSchedulingDetail && (
            <ModalCourseSchedulingSelect
              course_scheduling={selectCourseSchedulingDetail}
              onClose={closeModalSelectCourse}
            />
          )}
          {openBusinessReport ? (
            <ModalAttached
              open={openBusinessReport !== undefined}
              onClose={() => updateOpenBusinessReport(undefined)}
              defaultValue={openBusinessReport?.business_report}
              title="Informe empresarial final"
              onFileSaved={onBusinessReportSaved}
              category='business_report'
              disabled={openBusinessReport?.schedulingStatus?.name === 'Cancelado' || !userService.can('permission:course_scheduling_upload_business_report', 'module:course_scheduling')}
            />
          ): null}
          {openPartialReport ? (
            <ModalAttached
              open={openPartialReport !== undefined}
              onClose={() => updateOpenPartialReport(undefined)}
              defaultValue={openPartialReport?.partial_report}
              title="Informe empresarial parcial"
              onFileSaved={onPartialReportSaved}
              category='partial_report'
              disabled={openPartialReport?.schedulingStatus?.name === 'Cancelado' || !userService.can('permission:course_scheduling_upload_partial_report', 'module:course_scheduling')}
            />
          ): null}
          <ModalAttached
            open={openAttached !== undefined}
            onClose={() => updateOpenAttached(undefined)}
            defaultValue={openAttached}
            title="Adjuntos de operación"
            category='course_scheduling_attachments'
            observations={observations}
            disabled
          />
          <ModalAttached
            open={openAttachedStudent !== undefined}
            onClose={() => updateOpenAttachedStudent(undefined)}
            defaultValue={openAttachedStudent}
            title="Adjuntos de estudiantes"
            category='course_scheduling_attachments'
            disabled
          />
          {modalCourseSchedulingAssociate?.status && modalCourseSchedulingAssociate.courseSchedulingId && (
            <ModalCourseSchedulingAssociate
              open={modalCourseSchedulingAssociate?.status}
              courseSchedulingId={modalCourseSchedulingAssociate.courseSchedulingId}
              title='Programaciones relacionadas'
              onClose={(data) => onCloseModalCourseSchedulingAssociate(data)}
            />
          )}
        </div>
      </div>
    </AppContainer>
  )
}

interface IModalCourseSchedulingSelectProps {
  course_scheduling: string
  onClose: (selected: string | null) => void
}
const ModalCourseSchedulingSelect: React.FC<IModalCourseSchedulingSelectProps> = (props) => {

  const { t } = useTranslation()

  const [loading, setLoading] = useState<boolean>(false)
  const [showModal, setShowModal] = useState<boolean>(false)
  const [courseSchedulingDetailSelected, setCourseSchedulingDetailSelected] = useState<string | null>(null)
  const [courseSchedulingList, setCourseSchedulingList] = useState<Array<any>>([])

  const hideModal = (send: boolean) => {
    setShowModal(false)
    if (send) {
      props.onClose(courseSchedulingDetailSelected)
    } else {
      props.onClose(null)
    }
  }

  const getList = async () => {
    setLoading(true)
    const courseSchedulingDetailService: CourseSchedulingDetailsService = new CourseSchedulingDetailsService();
    const httpParams: any = {
      isActive: false,
      course_scheduling: props.course_scheduling
    }

    let itemList: ICourseSchedulingDetailsHttpResponse | null = await courseSchedulingDetailService.list(httpParams)

    if (itemList?.status_code === 'success') {
      setCourseSchedulingList(itemList?.schedulings)
      setLoading(false)
    } else {
      setLoading(false)
    }
  }

  useEffect(() => {
    setShowModal(true)
    getList()
  }, [])


  return (
    <Modal show={showModal} onHide={() => hideModal(false)} dialogClassName="modal_course_scheduling_select" size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{t('screens.course_scheduling.modal_course_scheduling_select.title')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div>
            <div className='spinner-border text-primary' role='status'>
              <span className='sr-only'>{t('loading')}</span>
            </div>
          </div>
        ): (
          <>
            <div>Selecciona el curso del cual deseas consultar la encuesta</div>
            {courseSchedulingList.map((course) => (
              <div className='form-check'>
                <input
                  className="form-check-input"
                  type="radio"
                  name="course_scheduling_detail"
                  id={`course_scheduling_detail_${course._id}`}
                  onClick={() => setCourseSchedulingDetailSelected(course._id)}
                  checked={courseSchedulingDetailSelected === course._id}
                />
                <label className='form-check-label' htmlFor={`course_scheduling_detail_${course._id}`}>
                  {course?.course?.name}
                </label>
              </div>
            ))}
            <div className="btn-generate-report">
              <button
                className="btn btn-primary"
                onClick={() => hideModal(true)}
                disabled={!courseSchedulingList}
              >
                Generar reporte
              </button>
            </div>
          </>
        )}
      </Modal.Body>
    </Modal>
  )
}

export default CourseSchedulingScreenLayout
